import { CheckCircle2, FileText, TableIcon } from "lucide-react";
import { Card, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Compare } from "@/components/ui/compare";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const pdfRows = [
  { text: "CHASE BANK STATEMENT", bold: true },
  { text: "Account Number: ****4821", bold: false },
  { text: "Statement Period: 01/01/2024 - 01/31/2024", bold: false },
  { text: "", bold: false },
  { text: "Beginning Balance              $3,241.88", bold: false },
  { text: "", bold: false },
  { text: "01/02  WHOLEFDS #10191 CHESTNUT H  -$67.43", bold: false },
  { text: "01/03  VENMO PAYMENT              -$120.00", bold: false },
  { text: "01/05  DIRECT DEPOSIT EMPLOYER     +$2,800.00", bold: false },
  { text: "01/07  AMAZON.COM*MT4XD9QH3        -$34.99", bold: false },
  { text: "01/08  ATM WITHDRAWAL              -$200.00", bold: false },
  { text: "01/09  NETFLIX.COM                 -$15.49", bold: false },
  { text: "01/11  WHOLEFDS #10191             -$54.21", bold: false },
  { text: "01/12  SHELL OIL 12345678          -$48.33", bold: false },
  { text: "        Ending Balance             $5,501.43", bold: false },
];

const excelRows = [
  { date: "01/02/2024", desc: "Whole Foods #10191", debit: "67.43", credit: "", bal: "3,174.45" },
  { date: "01/03/2024", desc: "Venmo Payment", debit: "120.00", credit: "", bal: "3,054.45" },
  { date: "01/05/2024", desc: "Direct Deposit", debit: "", credit: "2,800.00", bal: "5,854.45" },
  { date: "01/07/2024", desc: "Amazon.com", debit: "34.99", credit: "", bal: "5,819.46" },
  { date: "01/08/2024", desc: "ATM Withdrawal", debit: "200.00", credit: "", bal: "5,619.46" },
  { date: "01/09/2024", desc: "Netflix", debit: "15.49", credit: "", bal: "5,603.97" },
  { date: "01/11/2024", desc: "Whole Foods #10191", debit: "54.21", credit: "", bal: "5,549.76" },
  { date: "01/12/2024", desc: "Shell Oil", debit: "48.33", credit: "", bal: "5,501.43" },
];

const checks = ["Every row extracted", "Dates parsed correctly", "Debits & credits split"];

const stats = [
  { value: "Local", label: "PDF parsing in your browser" },
  { value: "Preview", label: "Review rows before export" },
  { value: ".xlsx", label: "Excel-ready workbook" },
  { value: "5 free", label: "Conversions on free plan" },
];

export function BeforeAfter() {
  return (
    <section className="bg-background px-4 py-16 md:py-24">
      <div className="mx-auto max-w-7xl">
        {/* Heading */}
        <div className="mb-12 text-center space-y-3">
          <Badge variant="outline" className="text-xs font-medium px-3 py-1">
            See the difference
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
            From impossible to read{" "}
            <span className="text-primary">→ instantly structured</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-sm md:text-base">
            Open a text-based statement, review the extracted rows, then export a clean workbook.
          </p>
        </div>

        {/* Compare slider: drag/hover to reveal PDF -> Excel */}
        <div className="flex flex-col items-center gap-4">
          <Card className="overflow-hidden shadow-sm p-0 w-full max-w-3xl">
            <Compare
              slideMode="drag"
              initialSliderPercentage={50}
              className="w-full h-[420px] md:h-[480px] rounded-none"
              firstContent={
                <div className="flex h-full flex-col bg-background">
                  <CardHeader className="px-4 py-3 bg-destructive/5 border-b border-destructive/15 space-y-0 shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="h-2.5 w-2.5 rounded-full bg-destructive/50" />
                          <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/50" />
                          <span className="h-2.5 w-2.5 rounded-full bg-green-400/50" />
                        </div>
                        <span className="font-mono text-[10px] text-muted-foreground">
                          chase_statement_jan_2024.pdf
                        </span>
                      </div>
                      <Badge variant="destructive" className="text-[10px] px-2 py-0 h-5 gap-1">
                        <FileText className="h-2.5 w-2.5" />
                        Before
                      </Badge>
                    </div>
                  </CardHeader>
                  <ScrollArea className="flex-1">
                    <div className="p-5 font-mono text-xs text-foreground/60 leading-6">
                      {pdfRows.map((row, i) => (
                        <div
                          key={i}
                          className={`whitespace-pre ${row.text === "" ? "h-3" : ""} ${
                            row.bold ? "text-foreground/80 font-bold" : ""
                          }`}
                        >
                          {row.text}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  <Separator />
                  <p className="py-2 text-center text-[11px] text-muted-foreground shrink-0">
                    Try copying this into Excel. We dare you.
                  </p>
                </div>
              }
              secondContent={
                <div className="flex h-full flex-col bg-background">
                  <CardHeader className="px-4 py-3 bg-[#217346]/5 border-b border-[#217346]/15 space-y-0 shrink-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <span className="h-2.5 w-2.5 rounded-full bg-[#217346]/50" />
                          <span className="h-2.5 w-2.5 rounded-full bg-[#217346]/30" />
                          <span className="h-2.5 w-2.5 rounded-full bg-[#217346]/20" />
                        </div>
                        <span className="font-mono text-[10px] text-[#217346]">
                          chase_statement_jan_2024.xlsx
                        </span>
                      </div>
                      <Badge className="text-[10px] px-2 py-0 h-5 gap-1 bg-[#217346] hover:bg-[#217346]/90">
                        <TableIcon className="h-2.5 w-2.5" />
                        After
                      </Badge>
                    </div>
                  </CardHeader>
                  <ScrollArea className="flex-1">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-[#217346]/8 hover:bg-[#217346]/10">
                          <TableHead className="text-xs font-semibold whitespace-nowrap">Date</TableHead>
                          <TableHead className="text-xs font-semibold whitespace-nowrap">Description</TableHead>
                          <TableHead className="text-xs font-semibold text-right whitespace-nowrap">Debit</TableHead>
                          <TableHead className="text-xs font-semibold text-right whitespace-nowrap">Credit</TableHead>
                          <TableHead className="text-xs font-semibold text-right whitespace-nowrap">Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {excelRows.map((row, i) => (
                          <TableRow key={i} className={i % 2 === 1 ? "bg-muted/20" : ""}>
                            <TableCell className="font-mono text-xs text-muted-foreground whitespace-nowrap py-2">
                              {row.date}
                            </TableCell>
                            <TableCell className="text-xs whitespace-nowrap max-w-[130px] truncate py-2">
                              {row.desc}
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono text-destructive/80 whitespace-nowrap py-2">
                              {row.debit ? `−${row.debit}` : ""}
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono text-green-600 whitespace-nowrap py-2">
                              {row.credit ? `+${row.credit}` : ""}
                            </TableCell>
                            <TableCell className="text-xs text-right font-mono text-muted-foreground whitespace-nowrap py-2">
                              {row.bal}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                  <Separator />
                  <div className="flex flex-wrap gap-3 justify-center py-3 px-4 shrink-0">
                    {checks.map((item) => (
                      <span key={item} className="inline-flex items-center gap-1.5 text-[11px] text-green-600 font-medium">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              }
            />
          </Card>
          <Badge variant="secondary" className="text-xs font-medium">
            Drag the slider to compare
          </Badge>
        </div>

        {/* Stat strip */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
          {stats.map((stat) => (
            <Card key={stat.label} className="text-center py-4 px-5 border-border/60 bg-muted/20">
              <p className="text-2xl font-bold text-primary">{stat.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export default BeforeAfter;
